import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Settings.css';

const policies = {
  terms: {
    title: '📋 Terms & Conditions',
    content: `Welcome to JustService24. By using our platform, you agree to the following terms:

1. ACCEPTANCE OF TERMS
By accessing JustService24, you agree to be bound by these Terms and Conditions.

2. USE OF SERVICE
JustService24 provides a platform to connect users with local service providers. You agree to use the service only for lawful purposes.

3. BUSINESS LISTINGS
Business owners are responsible for the accuracy of their listing information. JustService24 reserves the right to remove any listing that violates our policies.

4. USER ACCOUNTS
You are responsible for maintaining the confidentiality of your account credentials. You must be at least 18 years old to use this service.

5. INTELLECTUAL PROPERTY
All content on JustService24 is protected by copyright. You may not reproduce or distribute any content without permission.

6. LIMITATION OF LIABILITY
JustService24 is not liable for any damages arising from the use of our platform or services listed on it.

7. CHANGES TO TERMS
We reserve the right to modify these terms at any time. Continued use of the service constitutes acceptance of the new terms.

Contact us at: legal@justservice24.com`,
  },
  privacy: {
    title: '🔒 Privacy Policy',
    content: `JustService24 is committed to protecting your privacy.

1. INFORMATION WE COLLECT
- Phone number for authentication
- Profile information (name, email, address, occupation)
- Business listing details
- Location data (with your permission)
- Usage data and analytics

2. HOW WE USE YOUR INFORMATION
- To provide and improve our services
- To send important notifications
- To personalize your experience
- To process transactions

3. DATA SHARING
We do not sell your personal data to third parties. We may share data with:
- Service providers who assist in our operations
- Law enforcement when required by law

4. DATA SECURITY
We use industry-standard encryption to protect your data. However, no method of transmission over the internet is 100% secure.

5. YOUR RIGHTS
You have the right to:
- Access your personal data
- Correct inaccurate data
- Request deletion of your data
- Opt-out of marketing communications

6. COOKIES
We use cookies to improve your browsing experience. You can disable cookies in your browser settings.

Contact us at: privacy@justservice24.com`,
  },
  refund: {
    title: '💰 Refund Policy',
    content: `JustService24 Refund Policy

1. FREE SERVICES
Basic features like business listing, digital poster, and visiting card are completely free. No refund is applicable.

2. PAID SERVICES (AdSet)
- Refund requests must be made within 24 hours of purchase
- Refunds are processed within 5-7 business days
- Refunds are credited to the original payment method

3. NON-REFUNDABLE ITEMS
- Completed ad campaigns that have already been published
- Coins/rewards that have been redeemed

4. HOW TO REQUEST A REFUND
Contact our support team at support@justservice24.com with:
- Your registered phone number
- Transaction ID
- Reason for refund

5. DISPUTE RESOLUTION
If you are not satisfied with our refund decision, you may escalate the matter to our senior support team.

Contact us at: support@justservice24.com`,
  },
};

export default function AppSettings() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('English');
  const [saved, setSaved] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(null);

  const handleLanguage = (lang) => {
    setLanguage(lang);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (openPolicy) {
    const policy = policies[openPolicy];
    return (
      <div className="settings-page">
        <div className="settings-header">
          <div className="container">
            <button className="back-btn" onClick={() => setOpenPolicy(null)}>← Back</button>
            <h1>{policy.title}</h1>
          </div>
        </div>
        <div className="container">
          <div className="policy-content">
            <pre>{policy.content}</pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="container">
          <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
          <h1>App Settings</h1>
        </div>
      </div>

      <div className="container">
        {saved && <div className="settings-toast">✅ Language changed to {language}</div>}

        {/* Language */}
        <div className="settings-card">
          <h3>🌐 Select Language</h3>
          <div className="lang-options">
            <div className={`lang-option ${language === 'English' ? 'selected' : ''}`}
              onClick={() => handleLanguage('English')}>
              <span>🇬🇧</span>
              <span>English</span>
              <span className="lang-check">{language === 'English' ? '✓' : ''}</span>
            </div>
            <div className={`lang-option ${language === 'Hindi' ? 'selected' : ''}`}
              onClick={() => handleLanguage('Hindi')}>
              <span>🇮🇳</span>
              <span>Hindi</span>
              <span className="lang-check">{language === 'Hindi' ? '✓' : ''}</span>
            </div>
          </div>
        </div>

        {/* Policies */}
        <div className="settings-card">
          <h3>📄 Legal</h3>
          <div className="policy-list">
            <div className="policy-item" onClick={() => setOpenPolicy('terms')}>
              <span>📋</span> Terms & Conditions <span className="arrow">›</span>
            </div>
            <div className="policy-item" onClick={() => setOpenPolicy('privacy')}>
              <span>🔒</span> Privacy Policy <span className="arrow">›</span>
            </div>
            <div className="policy-item" onClick={() => setOpenPolicy('refund')}>
              <span>💰</span> Refund Policy <span className="arrow">›</span>
            </div>
          </div>
        </div>

        {/* App Info */}
        <div className="settings-card">
          <h3>ℹ️ App Info</h3>
          <div className="app-info-list">
            <div className="app-info-row"><span>Version</span><span>1.0.0</span></div>
            <div className="app-info-row"><span>Platform</span><span>Web</span></div>
            <div className="app-info-row"><span>Support</span><span>support@justservice24.com</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
